import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useGymBilling } from '../../../contexts/GymBillingContext';
import LoadingSpinner from '../../common/LoadingSpinner';

/**
 * Overlay blur su route premium quando trial scaduto o piano non attivo.
 * Anteprima: aggiungi ?blur_preview=1 all'URL (solo gestore palestra).
 */
export default function TrialFeatureBlur({ children, routeKey }) {
    const { t } = useTranslation();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { isGym } = useAuth();
    const { billing, loading, isRouteBlurred, isBlurrableRoute } = useGymBilling();

    const pathForBlur = routeKey
        ? `/gym/${routeKey.replace(/^\//, '')}`
        : location.pathname;

    const previewBlur = searchParams.get('blur_preview') === '1' && isGym();
    const routeCanBlur = isBlurrableRoute(routeKey || pathForBlur);
    const apiBlur = billing?.blur_features && isRouteBlurred(pathForBlur);
    const shouldBlur = previewBlur || apiBlur;

    if (loading && routeCanBlur && !previewBlur) {
        return (
            <div className="flex min-h-[min(70vh,600px)] items-center justify-center bg-gray-50 dark:bg-gray-950">
                <LoadingSpinner />
            </div>
        );
    }

    if (!shouldBlur) {
        return children;
    }

    return (
        <div className="relative isolate w-full min-h-[min(70vh,720px)]">
            <div
                className="pointer-events-none select-none blur-md brightness-75 scale-[1.01] max-h-[85vh] overflow-hidden"
                aria-hidden="true"
            >
                {children}
            </div>

            <div
                className="absolute inset-0 z-30 flex items-center justify-center p-4 sm:p-8 bg-[#1a1540]/75 backdrop-blur-md"
                role="dialog"
                aria-modal="true"
                aria-labelledby="trial-blur-title"
            >
                <div className="w-full max-w-lg rounded-2xl border border-white/20 bg-white dark:bg-gray-900 shadow-2xl p-6 sm:p-8 text-center">
                    {previewBlur && (
                        <p className="mb-3 text-xs font-medium rounded-full bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100 px-3 py-1 inline-block">
                            {t('gym.billing.blurPreviewBadge')}
                        </p>
                    )}
                    <Lock className="h-12 w-12 mx-auto text-indigo-600 dark:text-indigo-300 mb-4" />
                    <h2
                        id="trial-blur-title"
                        className="text-xl font-bold text-gray-900 dark:text-white"
                    >
                        {t('gym.billing.blurTitle')}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-white/80 mt-3 leading-relaxed">
                        {t('gym.billing.blurBody')}
                    </p>
                    <Link
                        to="/gym/contabilita"
                        className="inline-block mt-6 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 text-sm font-semibold shadow-lg"
                    >
                        {t('gym.billing.openAccounting')}
                    </Link>
                    {previewBlur && (
                        <p className="mt-4 text-xs text-gray-500 dark:text-white/50">
                            {t('gym.billing.blurPreviewHint')}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
